<template>
  <b-row
    id="ContentEditor"
    class="m-0 overflow-hidden"
    :class="[showFooter ? 'tab-content-fill' : 'tab-content']"
  >
    <b-col
      cols="4"
      class="bg-white p-0 border-right flex-fill h-100"
    >
      <div
        v-if="isLoading"
        class="h-100 align-items-center d-flex justify-content-center card-body"
      >
        <div class="loader mx-auto" />
      </div>
      <ShowcaseContentList
        :key="componentKey"
        :is-loading="isListLoading"
        :read-only="readOnly"
        :content-items="pendingArticles"
        :comments-counts="commentsCounts"
        @setContentItem="setContentItem"
        @setContentList="setContentList"
        @updateArticleList="updateArticleList"
        @setSelectedItem="setSelectedItem"
        :view-type="viewType"
        :selected-item="selectedItem"
      />
    </b-col>
    <b-col
      class="p-0 h-100 overflow-hidden"
    >
      <div
        id="view-panel-wrapper"
        class="h-100"
      >
        <!-- <header
          class="px-5 py-4 m-0 bg-white d-flex border-bottom"
        >
          <h5 class="m-1">
            Currently Viewing: {{ getSelectedCreativeTemplateName }}
          </h5>
        </header> -->
        <div
          v-if="isLoading"
          class="h-100 align-items-center d-flex justify-content-center card-body"
        >
          <div class="loader mx-auto" />
        </div>
        <div
          v-else
          ref="viewPoint"
          class="view-point d-flex flex-grow"
        >
          <CreativeCarousel
            :perspective="0"
            :controls-visible="true"
            :disable3d="true"
            :space="viewWidth"
            :display="creativesToDisplay"
            :width="viewWidth"
            :height="viewHeight"
            :one-directional="true"
            :start-index="startIndex"
            :is-buttons-disabled="isCarouselControlsDisabled"
            @goNext="nextCreative"
            @goPrevious="previousCreative"
          >
            <CarouselSlide
              v-for="(creative, key) in activeCreatives"
              :key="key"
              :index="key"
            >
              <RenderedTemplate
                ref="creativeRender"
                class="creative-slide"
                :read-only="readOnly"
                :is-creative-loading="isCreativeLoading"
                :creative-set="creative"
                :content-item="contentItem"
                :current-creative-id="currentCreativeId"
                :view-size="{ viewWidth:(viewWidth-160), viewHeight:(viewHeight-40)}"
              />
            </CarouselSlide>
          </CreativeCarousel>
        </div>
        <PreviewRightPanel
          :read-only="readOnly"
          :editable-properties="editableProperties"
          :view-type="viewType"
          :content-item="contentItem"
          @iframeLoading="iframeLoading()"
          @iframeLoad="rerenderCreative()"
        />
      </div>
    </b-col>
  </b-row>
</template>

<script>
import ShowcaseContentList from '@/modules/showcase/components/ShowcaseContentList';
import PreviewRightPanel from '@/modules/content/components/PreviewRightPanel/Sidebar';
import RenderedTemplate from '@/modules/templates/components/RenderedTemplate';
import CreativeCarousel from '@/modules/templates/components/CreativeCarousel';
import CarouselSlide from '@/modules/templates/components/CarouselSlide';

import {
  mapActions, mapGetters, mapMutations, mapState,
} from 'vuex';
import * as types from '@/modules/content/store/types';
import * as templatesTypes from '@/modules/templates/store/types';
import * as campaignTypes from '@/modules/campaigns/store/types';

export default {
  name: 'ContentShowcase',
  components: {
    ShowcaseContentList,
    PreviewRightPanel,
    CreativeCarousel,
    CarouselSlide,
    RenderedTemplate,
  },
  props: {
    showFooter: {
      type: Boolean,
      default: false,
    },
    readOnly: {
      type: Array,
      default: () => [],
    },
    viewType: {
      type: String,
      default: 'Showcase',
    },
    startIndex: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      iframeLoaded: false,
      selectedItem: '',
      campaignId: 0,
      isLoading: true,
      isListLoading: true,
      isCreativeLoading: true,
      pendingArticles: [],
      firstLoad: true,
      noSharedItems: false,
      componentKey: 0,
      availableTemplates: [],
      viewWidth: 700,
      viewHeight: 600,
    };
  },
  computed: {
    ...mapState('campaigns', [
      'campaign',
      'order',
      'selectedAdvertiser',
    ]),
    ...mapState('content', [
      'contentItems',
      'scrapedContent',
      'creative',
      'commentsCounts',
      'mode',
      'allowAnnotations',
      'currentAnnotation',
      'currentAnnotationState',
      'feedId',
      'activeCreatives',
    ]),
    ...mapGetters('content', {
      getArticles: types.GET_ARTICLES,
      templateOptions: types.GET_TEMPLATE_OPTIONS,
    }),
    ...mapGetters('templates', {
      editableProperties: templatesTypes.GET_TEMPLATE_EDITABLE_PROPERTIES,
    }),
    ...mapGetters('content', {
      getDisplayCopy: types.GET_CURRENT_SELECTED_CREATIVE_DISPLAY_COPY,
      getSelectedCreative: types.GET_CURRENT_SELECTED_CREATIVE,
    }),
    currentCreativeId() {
      return this.getSelectedCreative.id;
    },
    currentCreativeKey() {
      return this.getSelectedCreative.creativeKey;
    },
    contentItem() {
      if (this.selectedItem === '') return {};
      return Array.from(this.contentItems)
        .find((item) => item.encodedExternalId === this.selectedItem);
    },
    isCreativeSet() {
      return this.creative.id !== 0;
    },
    showEditPanel() {
      return this.selectedItem !== '';
    },
    getAdvertiserId() {
      if (this.selectedAdvertiser !== {}) {
        return this.selectedAdvertiser.id;
      }
      return 0;
    },
    creativesToDisplay() {
      return this.activeCreatives.length;
    },
    isLoadingContentDisplayCopy() {
      return (Object.keys(this.getCurrentSelectedCreativeDisplayCopy).length === 0);
    },
    getCurrentSelectedCreativeDisplayCopy() {
      if (this.isLoadingContentDisplayCopy) return {};
      const { encodedExternalId, id } = this.contentItem;
      return {
        ...this.getDisplayCopy(encodedExternalId),
        encodedExternalId,
        id,
      };
    },
    getSelectedCreativeTemplateName() {
      if (this.availableTemplates.length === 0) return '';
      const { name = '' } = this.availableTemplates
        .find((item) => item.id === this.getSelectedCreative.creativeTemplateId);
      return name;
    },
  },
  methods: {
    ...mapActions('content', [
      types.FETCH_CONTENT_COMMENTS_COUNT,
      types.FETCH_SHARED_COUNT,
      types.SET_CURRENT_ANNOTATION,
      types.REMOVE_CURRENT_ANNOTATION,
      types.UPDATE_CURRENT_SELECTED_CREATIVE_ID,
      types.FETCH_FILTERED_FEED_CONTENT_ITEMS,
      types.FETCH_CONTENT_DISPLAY_COPY,
    ]),
    ...mapActions('campaigns', [
      campaignTypes.SET_VIEW_TYPE,
    ]),
    ...mapActions('templates', [
      templatesTypes.FETCH_TEMPLATES_IN_GROUP_ID,
    ]),
    ...mapMutations('content', [
      types.CLEAR_CURRENT_COMMENTS,
    ]),
    setContentItem(id) {
      this.selectedItem = id;
      if (this.$refs.editPanel) {
        this.$refs.editPanel.setTimestamp();
      }
    },
    next() {
      this.$emit('next-step');
    },
    setContentList() {
      this.pendingArticles = this.contentItems;
      if (this.pendingArticles.length > 0 && this.selectedItem === '') {
        this.setContentItem(this.pendingArticles[0].encodedExternalId);
      }
    },
    async updateArticleList() {
      this.noSharedItems = false;
      this.isListLoading = true;
      if (this.contentItems.length === 0) {
        this.setContentList();
        this.selectedItem = '';
        this.isListLoading = false;
        this.isCarouselControlsDisabled = false;
        if (this.firstLoad) {
          this.noSharedItems = true;
          this.isLoading = false;
          this.firstLoad = false;
        }
        return;
      }
      await this.FETCH_CONTENT_COMMENTS_COUNT(this.creative.id);
      if (this.mode === 'SHARER') {
        this.FETCH_SHARED_COUNT();
      }
      this.setContentList();
      this.isLoading = false;
      this.isListLoading = false;
      this.isCreativeLoading = false;
      this.isCarouselControlsDisabled = false;
    },
    nextCreative() {
      this.isCarouselControlsDisabled = true;
      this.changeCreative(true);
    },
    previousCreative() {
      this.isCarouselControlsDisabled = true;
      this.changeCreative(false);
    },
    async changeCreative(next) {
      this.isCarouselControlsDisabled = true;
      const previousFeedId = this.getSelectedCreative.feedId;
      this.UPDATE_CURRENT_SELECTED_CREATIVE_ID(next);
      if (previousFeedId !== this.getSelectedCreative.feedId) {
        this.setSelectedItem('');
      }
      this.isCreativeLoading = true;
      await this.FETCH_FILTERED_FEED_CONTENT_ITEMS({ feedId: this.getSelectedCreative.feedId });
      this.updateArticleList();
      this.updateRoute();
    },
    setSelectedItem(id) {
      this.selectedItem = id;
    },
    refreshContentList() {
      this.pendingArticles = this.getArticles(false);
    },
    refreshEditor() {
      this.componentKey += 1;
      this.updateArticleList();
    },
    iframeLoading() {
      this.iframeLoaded = false;
    },
    rerenderCreative() {
      const { id } = this.getSelectedCreative;
      const currentCreativeIndex = this.$refs.creativeRender
        .findIndex((item) => item.creativeSet.id === id);
      this.$refs.creativeRender[currentCreativeIndex].rerenderCreative();
    },
    updateRoute() {
      this.$router
        .replace({
          name: 'Campaign Showcase',
          params: { campaignId: this.campaignId },
          query: { creativeKey: this.currentCreativeKey, selectedItem: this.selectedItem },
        });
    },
  },
  async created() {
    this.isCreativeLoading = true;
    this.SET_VIEW_TYPE('Showcase');
    const { campaignId } = this.$route.params;
    const { selectedItem } = this.$route.query;
    if (campaignId) this.campaignId = campaignId;
    if (selectedItem) this.selectedItem = selectedItem;
    await this.FETCH_CONTENT_DISPLAY_COPY(false);
    await this.updateArticleList();
    this.isCarouselControlsDisabled = false;
  },
};
</script>
<style lang="scss" scoped>
.tab-content {
  height: 100%;
  overflow: hidden;
}
.tab-content-fill {
  // height: calc(100% - 65px);
  height: calc(100%);
  // padding-bottom: 69px;
}
.view-point{
  margin-right: 58px;
  overflow: auto;
  // height: calc(100% - 58px);
  height: calc(100%);
}
.creative-wrapper {
  position: relative;
  margin-bottom: 14px;
  background: rgba(0, 0, 0, 0.2);
  box-shadow: -10px 10px 10px rgba(0, 0, 0, 0.3);
  -moz-box-shadow: -10px 10px 10px rgba(0, 0, 0, 0.3);
  -webkit-box-shadow: -10px 10px 10px rgba(0, 0, 0, 0.3);
}
</style>
